import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { FuelCellGrid } from "./components/FuelCellGrid";
import { FuelCellSummary } from "./components/FuelCellSummary";
import { FuelCellUsageChart } from "./components/FuelCellUsageChart";
import { useFuelCellData } from "./hooks/useFuelCellData";
import { useFuelCellFromToDate } from "./hooks/useFuelCellFromToDate";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { mapLazy } from "@/utilities/mapLazy";

const FilterStackLayout = mapLazy(
  "FilterStackLayout",
  import("@/components/layout/FilterStackLayout"),
);

export function FuelCellPage() {
  const { t } = useTranslation();
  const { fromDate, toDate, setFromDate, setToDate } = useFuelCellFromToDate();
  const {
    rows,
    samplesByDevice,
    isLoading,
    isFetching,
    error,
    groupMissing,
    truncatedDeviceNames,
    summaryStats,
  } = useFuelCellData(fromDate, toDate);

  return (
    <FilterStackLayout
      minHeight={60}
      filters={
        <DateRangeSelect
          fromDate={fromDate}
          toDate={toDate}
          onChange={(from, to) => {
            if (from) setFromDate(from);
            if (to) setToDate(to);
          }}
        />
      }
      content={
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ mx: 2 }}>
              {error.message}
            </Alert>
          )}
          {groupMissing && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t("fuelCell.groupMissing", "Group '07 Fuel Cell' not found in this database")}
            </Alert>
          )}
          {truncatedDeviceNames.length > 0 && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t(
                "fuelCell.rangeTruncated",
                "Data limit reached for some vehicles — usage and refills may be incomplete. Try a shorter time range. Affected: {{names}}",
                { names: truncatedDeviceNames.join(", ") },
              )}
            </Alert>
          )}
          {rows.length > 0 && <FuelCellSummary stats={summaryStats} />}
          {rows.length > 0 && <FuelCellUsageChart rows={rows} />}
          <FuelCellGrid
            rows={rows}
            samplesByDevice={samplesByDevice}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </Box>
      }
    />
  );
}
